import { ThemeProvider } from "@furistic/sdk-fe-ui-app-shell/providers/theme-provider";
import { LandingFooter, LandingNavbar } from "@furistic/sdk-fe-ui-landing";
import type { ReactNode } from "react";
import {
  BrowserRouter,
  MemoryRouter,
  Navigate,
  Route,
  Routes,
} from "react-router-dom";
import { chrome } from "@/chrome";
import { LandingChatbotWidget } from "@/components/LandingChatbotWidget";
import { ObservabilityInit } from "@/components/observability-init";
import { routes } from "@/routes";
import { Seo } from "@/seo";

/**
 * App shell: theme provider, shared landing chrome (navbar/footer from
 * `@furistic/sdk-fe-ui-landing`), the route table, and the chat widget.
 * Product identity lives in `@/chrome`; page content in `@/routes`.
 */
function Shell() {
  return (
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <Seo />
      <ObservabilityInit />
      <LandingChatbotWidget />
      <LandingNavbar config={chrome} />
      <Routes>
        {routes.map((route) => (
          <Route key={route.path} path={route.path} element={route.element} />
        ))}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <LandingFooter config={chrome} />
    </ThemeProvider>
  );
}

export interface AppProps {
  /**
   * URL to render. Set only during build-time prerender (the seo plugin calls
   * `render(path)` with no browser location available) — the app then runs on
   * a MemoryRouter. Omitted in the browser, where BrowserRouter takes over.
   */
  url?: string;
}

export function App({ url }: AppProps = {}) {
  if (url !== undefined) {
    return (
      <MemoryRouter initialEntries={[url]}>
        <Shell />
      </MemoryRouter>
    );
  }
  return (
    <BrowserRouter>
      <Shell />
    </BrowserRouter>
  );
}

/** Prerender entry for the seo plugin: `render: (path) => <App url={path} />`. */
export function renderApp(path: string): ReactNode {
  return <App url={path} />;
}
